import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateRequest } from '@/actions/wizard/RequestWizardActions';
import useValidation from '@/hooks/wizard/useValidation';
import useCountUp from './useCountUp';

const DEFAULT_TIER = 2;

const TIERS = [
    {
        value: 1,
        key: 'relaxed',
        label: 'Relaxed',
        blurb: 'Crowd-pleasing menus, relaxed service',
    },
    {
        value: 2,
        key: 'elevated',
        label: 'Elevated',
        blurb: 'Restaurant-quality three courses',
        popular: true,
    },
    {
        value: 3,
        key: 'luxe',
        label: 'Luxe',
        blurb: 'Michelin-trained & celebrity chefs',
    },
];

export default function LivePricingOptions() {
    const dispatch = useDispatch();
    const { completeStep } = useValidation();
    const { meta, request, options } = useSelector(
        (state) => state.requestWizard
    );

    const tiers = options.dynamicPricing?.tiers;
    const priced = TIERS.every((tier) => tiers?.[tier.key]);
    const calculating = options.dynamicPricingLoading && !priced;
    const selected = TIERS.find((tier) => tier.value === request.budgetType);

    // The model prices a single date; a multi-date request books the chef for
    // each of them, so the total covers every day (as the bands did).
    const days = Math.max(1, request.mealDate?.length || 1);
    const priceFor = (tier) => {
        const price = tiers?.[tier.key];

        return price ? { pp: price.pp, total: price.total * days } : null;
    };

    // Per-person and total interleaved so a single animation frame loop drives
    // every figure on screen.
    const targets = priced
        ? TIERS.flatMap((tier) => {
              const price = priceFor(tier);

              return [price.pp, price.total];
          })
        : null;
    const counted = useCountUp(targets);

    useEffect(() => {
        if (!selected) dispatch(updateRequest('budgetType', DEFAULT_TIER));
    }, [request.budgetType]);

    // The chosen tier IS the budget now that the step has no budget field:
    // submit and the chef count both still read request.budget. Keyed on the
    // prices too, so a refetch cannot leave the old budget behind the new
    // figures on screen. The step stays incomplete until a price exists --
    // submit rejects a missing budget.
    const selectedPrice = selected ? priceFor(selected) : null;

    useEffect(() => {
        if (!selectedPrice) return;

        dispatch(
            updateRequest({
                budget: selectedPrice.total,
                budget_pp: selectedPrice.pp,
            })
        );
        completeStep();
    }, [selected?.value, selectedPrice?.pp, selectedPrice?.total]);

    const money = (amount) =>
        `${meta.currencySign}${Math.round(amount).toLocaleString()}`;

    const calculatingCopy = meta.isMobile
        ? 'Checking live availability…'
        : `Checking live chef availability${
              request.postcode ? ` in ${request.postcode}` : ''
          }…`;

    return (
        <div className="wizard-live-pricing">
            {calculating && (
                <div className="wizard-live-pricing__status -calculating">
                    <span className="wizard-live-pricing__spinner" />
                    {calculatingCopy}
                </div>
            )}
            {priced && (
                <div className="wizard-live-pricing__status -live">
                    <span className="wizard-live-pricing__dot" />
                    Live prices for your event
                </div>
            )}
            <div
                className="wizard-live-pricing__options"
                role="radiogroup"
                aria-label="Pricing">
                {TIERS.map((tier, index) => {
                    const isSelected = selected?.value === tier.value;
                    const pp = counted?.[index * 2];
                    const total = counted?.[index * 2 + 1];

                    return (
                        <button
                            key={tier.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            onClick={() =>
                                dispatch(
                                    updateRequest('budgetType', tier.value)
                                )
                            }
                            className={`wizard-live-pricing__card${
                                tier.popular ? ' -popular' : ''
                            }${isSelected ? ' -selected' : ''}`}>
                            <span className="wizard-live-pricing__head">
                                <span className="wizard-live-pricing__label">
                                    {tier.label}
                                </span>
                                {tier.popular && (
                                    <span className="wizard-live-pricing__badge">
                                        Most popular
                                    </span>
                                )}
                            </span>
                            <span
                                className="wizard-live-pricing__radio"
                                aria-hidden="true"
                            />
                            <span className="wizard-live-pricing__price">
                                {calculating ? (
                                    <span
                                        className="wizard-live-pricing__shimmer"
                                        aria-label="Loading price"
                                    />
                                ) : (
                                    pp != null && `${money(pp)}pp`
                                )}
                            </span>
                            <span className="wizard-live-pricing__total">
                                {total != null && `${money(total)} total`}
                            </span>
                            <span className="wizard-live-pricing__blurb">
                                {tier.blurb}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
